import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import {
  FiBox, FiDownload, FiPrinter, FiSearch, FiAlertTriangle,
  FiPackage, FiMapPin, FiTrendingUp, FiDollarSign, FiClock
} from 'react-icons/fi';
import {
  BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import { colors } from '../../theme/colors';
import { collection, getDocs, query, where, orderBy } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { getLocationsCached } from '../../utils/masterDataStore';
import { ACTIVE_STOCK_STATUSES } from '../../utils/stockFlowHelper';
import { useAuth } from '../../context/AuthContext';
import PageLayout from '../../components/PageLayout';
import { formatCurrency, formatNumber } from '../../utils/formatters';
import useCurrency from '../../hooks/useCurrency';
import { addPrintBrand } from '../../utils/printBranding';

const CHART_COLORS = [
  colors.chart.primary,
  colors.chart.secondary,
  colors.chart.tertiary,
  colors.chart.accent1,
  colors.chart.accent2,
  colors.chart.accent3,
  '#4A90D9',
  '#E67E22',
  '#2ECC71',
  '#9B59B6'
];

const SEVERITY_CONFIG = {
  critical: { color: colors.error.main, bg: '#FFEBEE', label: 'Critical' },
  high: { color: colors.warning.main, bg: '#FFF8E1', label: 'High' },
  medium: { color: colors.info.main, bg: '#E0F7FA', label: 'Medium' },
  low: { color: colors.success.main, bg: '#E8F5E9', label: 'Low' }
};

const formatAmount = (amount) => {
  const dp = (typeof window !== 'undefined' && Number.isFinite(Number(window.__AMAL_DECIMAL_PLACES__)))
    ? Number(window.__AMAL_DECIMAL_PLACES__) : 2;
  if (!amount && amount !== 0) return dp > 0 ? '0.' + '0'.repeat(dp) : '0';
  return Number(amount).toLocaleString('en-PK', {
    minimumFractionDigits: dp,
    maximumFractionDigits: dp
  });
};

const formatDateStr = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-PK', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
};

const daysBetween = (date1, date2) => {
  const d1 = new Date(date1);
  const d2 = new Date(date2);
  const diffMs = Math.abs(d2 - d1);
  return Math.floor(diffMs / (1000 * 60 * 60 * 24));
};

const getSeverity = (currentQty, reorderPoint) => {
  if (!reorderPoint || reorderPoint <= 0) return 'low';
  const ratio = currentQty / reorderPoint;
  if (ratio <= 0) return 'critical';
  if (ratio <= 0.25) return 'critical';
  if (ratio <= 0.5) return 'high';
  if (ratio <= 0.75) return 'medium';
  return 'low';
};

const InventoryIntelligenceReport = () => {
  const navigate = useNavigate();
  const { activeCompany, activeFinancialYear, activeBranch } = useAuth();
  const { formatCurrency } = useCurrency();

  // Data states
  const [stockFlows, setStockFlows] = useState([]);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [inventoryLocations, setInventoryLocations] = useState([]);
  const [categoryLookup, setCategoryLookup] = useState({});
  const [financialYears, setFinancialYears] = useState([]);

  // Filter states
  const [selectedFYId, setSelectedFYId] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedSeverity, setSelectedSeverity] = useState('All');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  // UI states
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [sortField, setSortField] = useState('product');
  const [sortDirection, setSortDirection] = useState('asc');
  const [activeTable, setActiveTable] = useState('reorder');

  // Load financial years
  const loadFinancialYears = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const fyRef = collection(db, 'companies', activeCompany.id, 'financialYears');
      const q = query(fyRef, orderBy('startDate', 'desc'));
      const snapshot = await getDocs(q);
      const fys = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      setFinancialYears(fys);

      if (activeFinancialYear?.id) {
        setSelectedFYId(activeFinancialYear.id);
      } else if (fys.length > 0) {
        setSelectedFYId(fys[0].id);
      }
    } catch (error) {
      console.error('Error loading financial years:', error);
    }
  }, [activeCompany, activeFinancialYear]);

  // Load inventory items
  const loadInventoryItems = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const itemsRef = collection(db, 'companies', activeCompany.id, 'inventoryItems');
      const q = query(itemsRef, orderBy('itemName', 'asc'));
      const snapshot = await getDocs(q);
      setInventoryItems(snapshot.docs.map(d => ({ id: d.id, ...d.data() })).filter(i => i.isDeleted !== true));

      // Load categories for lookup
      const catRef = collection(db, 'companies', activeCompany.id, 'inventoryCategories');
      const catSnapshot = await getDocs(catRef);
      const catMap = {};
      catSnapshot.docs.forEach(d => { catMap[d.id] = d.data().categoryName || d.data().categoryCode || 'Unknown'; });
      setCategoryLookup(catMap);
    } catch (error) {
      console.error('Error loading inventory items:', error);
    }
  }, [activeCompany]);

  // Load inventory locations
  const loadInventoryLocations = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const locRows = await getLocationsCached(activeCompany.id);
      setInventoryLocations([...locRows]);
    } catch (error) {
      console.error('Error loading inventory locations:', error);
    }
  }, [activeCompany]);

  useEffect(() => {
    loadFinancialYears();
    loadInventoryItems();
    loadInventoryLocations();
  }, [loadFinancialYears, loadInventoryItems, loadInventoryLocations]);

  // Categories derived from categoryLookup
  const categories = useMemo(() => {
    return Object.values(categoryLookup).sort();
  }, [categoryLookup]);

  // Load stock flow data
  const loadStockFlows = async () => {
    if (!activeCompany?.id) return;

    setLoading(true);
    setHasSearched(true);

    try {
      const sfRef = collection(db, 'companies', activeCompany.id, 'stockFlow');
      const conditions = [where('status', 'in', ACTIVE_STOCK_STATUSES)];

      if (selectedFYId) {
        conditions.push(where('financialYearId', '==', selectedFYId));
      }

      const q = query(sfRef, ...conditions);
      const snapshot = await getDocs(q);

      let allFlows = snapshot.docs.map(d => ({ id: d.id, ...d.data() }))
        .filter(t => {
          if (!activeBranch || activeBranch.id === 'all') return true;
          return t.branchId === activeBranch.id;
        });

      // INV-07: keep rows before 'From' — stock on hand/value must include them
      // (same as Inventory Balances). Movement stats below still use the period only.
      if (toDate) allFlows = allFlows.filter(t => (t.docDate || '') <= toDate);

      allFlows.sort((a, b) => (b.docDate || '').localeCompare(a.docDate || ''));

      setStockFlows(allFlows);
    } catch (error) {
      console.error('Error loading stock flows:', error);
    } finally {
      setLoading(false);
    }
  };

  // Compute stock summary from stockFlow
  const stockSummary = useMemo(() => {
    const itemMap = {};
    const balances = {};
    const now = new Date();

    stockFlows.forEach(flow => {
      const itemId = flow.itemId;
      if (!itemId) return;
      // INV-07: running balance (qty + value) from every row up to 'To'.
      const bq = Number(flow.quantity) || 0;
      const bc = Number(flow.totalCost) || 0;
      balances[itemId] = balances[itemId] || { qty: 0, value: 0 };
      if (flow.direction === 'in') { balances[itemId].qty += bq; balances[itemId].value += bc; }
      else if (flow.direction === 'out') { balances[itemId].qty -= bq; balances[itemId].value -= bc; }
      if (fromDate && (flow.docDate || '') < fromDate) return; // period stats only below

      if (!itemMap[itemId]) {
        itemMap[itemId] = {
          itemId,
          qtyIn: 0,
          qtyOut: 0,
          costIn: 0,
          costOut: 0,
          lastMovement: null,
          earliestIn: null,
          locationIds: new Set(),
          inDates: []
        };
      }

      const qty = Number(flow.quantity) || 0;
      const cost = Number(flow.totalCost) || 0;
      const docDate = flow.docDate || '';

      if (flow.direction === 'in') {
        itemMap[itemId].qtyIn += qty;
        itemMap[itemId].costIn += cost;
        itemMap[itemId].inDates.push(docDate);
        if (!itemMap[itemId].earliestIn || docDate < itemMap[itemId].earliestIn) {
          itemMap[itemId].earliestIn = docDate;
        }
      } else if (flow.direction === 'out') {
        itemMap[itemId].qtyOut += qty;
        itemMap[itemId].costOut += cost;
      }

      if (!itemMap[itemId].lastMovement || docDate > itemMap[itemId].lastMovement) {
        itemMap[itemId].lastMovement = docDate;
      }

      if (flow.locationId) {
        itemMap[itemId].locationIds.add(flow.locationId);
      }
    });

    // Build item details
    const itemDetailsMap = {};
    inventoryItems.forEach(item => {
      itemDetailsMap[item.id] = item;
    });

    // Items with balance but no movement inside the period still show.
    Object.keys(balances).forEach(itemId => {
      if (!itemMap[itemId]) itemMap[itemId] = { itemId, qtyIn: 0, qtyOut: 0, costIn: 0, costOut: 0, lastMovement: null, earliestIn: null, locationIds: new Set(), inDates: [] };
    });

    const results = Object.values(itemMap).map(entry => {
      const item = itemDetailsMap[entry.itemId] || {};
      // INV-07: same valuation as Inventory Balances — value in minus value out
      // (never below zero); average = value ÷ quantity on hand.
      const bal = balances[entry.itemId] || { qty: 0, value: 0 };
      const currentQty = Math.round(bal.qty * 1e6) / 1e6;
      const currentValue = Math.max(0, Math.round(bal.value * 100) / 100);
      const avgCost = currentQty > 0 ? currentValue / currentQty : 0;
      const lastMovement = entry.lastMovement;
      const daysSinceMovement = lastMovement ? daysBetween(lastMovement, now.toISOString().split('T')[0]) : 999;
      const reorderPoint = Number(item.reorderQuantity) || 0;
      const severity = getSeverity(currentQty, reorderPoint);

      // Compute days until stockout based on recent outflow rate
      const avgDailyOut = entry.qtyOut > 0 && entry.earliestIn
        ? entry.qtyOut / Math.max(1, daysBetween(entry.earliestIn, now.toISOString().split('T')[0]))
        : 0;
      const daysUntilStockout = avgDailyOut > 0 ? Math.round(currentQty / avgDailyOut) : currentQty > 0 ? 999 : 0;

      // Aging buckets from inflow dates
      const agingBucket = (() => {
        if (entry.inDates.length === 0) return '90+';
        const latestIn = entry.inDates.sort().reverse()[0];
        const days = daysBetween(latestIn, now.toISOString().split('T')[0]);
        if (days <= 30) return '0-30';
        if (days <= 60) return '31-60';
        if (days <= 90) return '61-90';
        return '90+';
      })();

      return {
        itemId: entry.itemId,
        product: item.itemName || 'Unknown',
        sku: item.itemCode || '-',
        category: categoryLookup[item.categoryId] || 'Uncategorized',
        currentQty,
        currentValue,
        avgCost,
        reorderPoint,
        daysUntilStockout,
        daysSinceMovement,
        lastMovement,
        severity,
        agingBucket,
        qtyIn: entry.qtyIn,
        qtyOut: entry.qtyOut,
        costIn: entry.costIn,
        costOut: entry.costOut,
        locationIds: entry.locationIds
      };
    });

    return results;
  }, [stockFlows, inventoryItems, fromDate]);

  // Filtered data
  const filteredData = useMemo(() => {
    let data = [...stockSummary];

    if (selectedCategory) {
      data = data.filter(d => d.category === selectedCategory);
    }

    if (selectedSeverity !== 'All') {
      data = data.filter(d => d.severity === selectedSeverity.toLowerCase());
    }

    return data;
  }, [stockSummary, selectedCategory, selectedSeverity]);

  // Summary calculations
  const summary = useMemo(() => {
    const itemsWithStock = filteredData.filter(d => d.currentQty > 0);
    const totalSKUs = itemsWithStock.length;
    const totalStockValue = filteredData.reduce((sum, d) => sum + (d.currentValue || 0), 0);
    const belowReorder = filteredData.filter(d => d.currentQty <= d.reorderPoint && d.reorderPoint > 0).length;
    const deadStockItems = filteredData.filter(d => d.daysSinceMovement >= 90);
    const deadStockValue = deadStockItems.reduce((sum, d) => sum + (d.currentValue || 0), 0);

    // Average turnover: total COGS / avg inventory value
    const totalCOGS = filteredData.reduce((sum, d) => sum + (d.costOut || 0), 0);
    const totalAvgInventory = filteredData.reduce((sum, d) => {
      const avgInv = ((d.costIn || 0) + (d.currentValue || 0)) / 2;
      return sum + avgInv;
    }, 0);
    const avgTurnover = totalAvgInventory > 0 ? totalCOGS / totalAvgInventory : 0;

    // Active locations
    const allLocations = new Set();
    filteredData.forEach(d => {
      if (d.currentQty > 0) {
        d.locationIds.forEach(loc => allLocations.add(loc));
      }
    });
    const activeLocations = allLocations.size;

    return { totalSKUs, totalStockValue, belowReorder, deadStockValue, avgTurnover, activeLocations };
  }, [filteredData]);

  // Chart data: Stock Levels by Category
  const categoryStockData = useMemo(() => {
    const catMap = {};
    filteredData.forEach(d => {
      const cat = d.category || 'Uncategorized';
      if (!catMap[cat]) {
        catMap[cat] = { name: cat, totalQty: 0, totalCapacity: 0, value: 0, count: 0 };
      }
      catMap[cat].totalQty += d.currentQty;
      catMap[cat].totalCapacity += d.qtyIn;
      catMap[cat].value += d.currentValue;
      catMap[cat].count += 1;
    });

    return Object.values(catMap).map(c => ({
      ...c,
      stockPercent: c.totalCapacity > 0 ? Math.round((c.totalQty / c.totalCapacity) * 100) : 0
    })).sort((a, b) => b.value - a.value).slice(0, 10);
  }, [filteredData]);

  // Chart data: Inventory Aging
  const agingData = useMemo(() => {
    const buckets = { '0-30': 0, '31-60': 0, '61-90': 0, '90+': 0 };
    filteredData.forEach(d => {
      if (d.currentValue > 0) {
        buckets[d.agingBucket] = (buckets[d.agingBucket] || 0) + d.currentValue;
      }
    });
    return Object.entries(buckets).map(([name, value]) => ({ name, value }));
  }, [filteredData]);

  // Chart data: Turnover by Category
  const turnoverData = useMemo(() => {
    const catMap = {};
    filteredData.forEach(d => {
      const cat = d.category || 'Uncategorized';
      if (!catMap[cat]) {
        catMap[cat] = { name: cat, costOut: 0, avgInventory: 0 };
      }
      catMap[cat].costOut += d.costOut || 0;
      catMap[cat].avgInventory += ((d.costIn || 0) + (d.currentValue || 0)) / 2;
    });

    return Object.values(catMap).map(c => ({
      name: c.name,
      turnover: c.avgInventory > 0 ? Number((c.costOut / c.avgInventory).toFixed(2)) : 0
    })).sort((a, b) => b.turnover - a.turnover).slice(0, 10);
  }, [filteredData]);

  // Chart data: Stock by Location
  const locationData = useMemo(() => {
    const locMap = {};
    const locNameMap = {};
    inventoryLocations.forEach(loc => {
      locNameMap[loc.id] = loc.locationName || loc.name || loc.id;
    });

    filteredData.forEach(d => {
      if (d.currentQty > 0) {
        d.locationIds.forEach(locId => {
          const locName = locNameMap[locId] || locId;
          if (!locMap[locName]) {
            locMap[locName] = { name: locName, value: 0 };
          }
          locMap[locName].value += d.currentValue;
        });
      }
    });

    return Object.values(locMap).sort((a, b) => b.value - a.value);
  }, [filteredData, inventoryLocations]);

  // Reorder alerts table data
  const reorderAlerts = useMemo(() => {
    return filteredData
      .filter(d => d.currentQty <= d.reorderPoint && d.reorderPoint > 0)
      .sort((a, b) => {
        const severityOrder = { critical: 0, high: 1, medium: 2, low: 3 };
        return (severityOrder[a.severity] || 4) - (severityOrder[b.severity] || 4);
      });
  }, [filteredData]);

  // Dead stock table data
  const deadStockItems = useMemo(() => {
    return filteredData.filter(d => d.daysSinceMovement >= 90);
  }, [filteredData]);

  // Category summary table data
  const categorySummaryData = useMemo(() => {
    const catMap = {};
    const totalValue = filteredData.reduce((sum, d) => sum + (d.currentValue || 0), 0);

    filteredData.forEach(d => {
      const cat = d.category || 'Uncategorized';
      if (!catMap[cat]) {
        catMap[cat] = { category: cat, skus: 0, value: 0, costOut: 0, avgInventory: 0 };
      }
      catMap[cat].skus += 1;
      catMap[cat].value += d.currentValue || 0;
      catMap[cat].costOut += d.costOut || 0;
      catMap[cat].avgInventory += ((d.costIn || 0) + (d.currentValue || 0)) / 2;
    });

    return Object.values(catMap).map(c => ({
      ...c,
      stockPercent: totalValue > 0 ? Number(((c.value / totalValue) * 100).toFixed(1)) : 0,
      turnoverRate: c.avgInventory > 0 ? Number((c.costOut / c.avgInventory).toFixed(2)) : 0,
      status: c.avgInventory > 0
        ? (c.costOut / c.avgInventory >= 2 ? 'Healthy' : c.costOut / c.avgInventory >= 1 ? 'Normal' : 'Slow')
        : 'No Data'
    })).sort((a, b) => b.value - a.value);
  }, [filteredData]);

  // Sort handler
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortIndicator = (field) => {
    if (sortField !== field) return '';
    return sortDirection === 'asc' ? ' \u2191' : ' \u2193';
  };

  // Generic sort function
  const sortData = (data, numericFields = []) => {
    const sorted = [...data];
    sorted.sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];

      if (numericFields.includes(sortField)) {
        aVal = Number(aVal) || 0;
        bVal = Number(bVal) || 0;
      } else {
        aVal = String(aVal || '').toLowerCase();
        bVal = String(bVal || '').toLowerCase();
      }

      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  };

  // Sorted table data
  const sortedReorderAlerts = useMemo(() => {
    return sortData(reorderAlerts, ['currentQty', 'reorderPoint', 'daysUntilStockout']);
  }, [reorderAlerts, sortField, sortDirection]);

  const sortedDeadStock = useMemo(() => {
    return sortData(deadStockItems, ['currentValue', 'daysSinceMovement']);
  }, [deadStockItems, sortField, sortDirection]);

  const sortedCategorySummary = useMemo(() => {
    return sortData(categorySummaryData, ['skus', 'stockPercent', 'value', 'turnoverRate']);
  }, [categorySummaryData, sortField, sortDirection]);

  // CSV Export
  const handleExport = () => {
    if (filteredData.length === 0) return;

    const selectedFY = financialYears.find(f => f.id === selectedFYId);

    let csv = 'Inventory Intelligence Report\n';
    csv += `Company: ${activeCompany?.companyName || ''}\n`;
    csv += `Financial Year: ${selectedFY?.yearName || selectedFY?.yearCode || ''}\n`;
    if (fromDate || toDate) {
      csv += `Date Range: ${fromDate || 'Start'} to ${toDate || 'End'}\n`;
    }
    csv += `Generated: ${new Date().toLocaleString()}\n\n`;
    csv += `Total SKUs,${summary.totalSKUs}\n`;
    csv += `Total Stock Value,${summary.totalStockValue}\n`;
    csv += `Items Below Reorder,${summary.belowReorder}\n`;
    csv += `Dead Stock Value,${summary.deadStockValue}\n`;
    csv += `Avg Turnover,${summary.avgTurnover.toFixed(2)}\n`;
    csv += `Active Locations,${summary.activeLocations}\n\n`;

    // Reorder Alerts
    csv += 'REORDER ALERTS\n';
    csv += 'Product,SKU,Current Stock,Reorder Point,Days Until Stockout,Severity\n';
    reorderAlerts.forEach(d => {
      csv += `"${(d.product || '').replace(/"/g, '""')}","${d.sku}",${d.currentQty},${d.reorderPoint},${d.daysUntilStockout},"${d.severity}"\n`;
    });

    csv += '\nDEAD STOCK\n';
    csv += 'Product,SKU,Value,Days Stagnant,Last Movement\n';
    deadStockItems.forEach(d => {
      csv += `"${(d.product || '').replace(/"/g, '""')}","${d.sku}",${d.currentValue},${d.daysSinceMovement},"${formatDateStr(d.lastMovement)}"\n`;
    });

    csv += '\nCATEGORY SUMMARY\n';
    csv += 'Category,SKUs,Stock %,Value,Turnover Rate,Status\n';
    categorySummaryData.forEach(d => {
      csv += `"${(d.category || '').replace(/"/g, '""')}",${d.skus},${d.stockPercent}%,${d.value},${d.turnoverRate},"${d.status}"\n`;
    });

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Inventory_Intelligence_Report_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // Print
  const handlePrint = () => {
    const selectedFY = financialYears.find(f => f.id === selectedFYId);
    const w = window.open('', '_blank');
    w.document.write(`<html><head><title>Inventory Intelligence Report</title>
      <style>
        body{font-family:'Segoe UI',sans-serif;margin:30px;color:#1A1A1A;font-size:12px}
        h1{color:#800020;font-size:20px;margin-bottom:5px}
        h2{color:#800020;font-size:16px;margin-top:25px;margin-bottom:10px}
        .meta{color:#666;font-size:11px;margin-bottom:15px}
        .summary-grid{display:flex;gap:15px;margin-bottom:20px;flex-wrap:wrap}
        .summary-box{padding:12px 18px;background:#f9f9f9;border-radius:6px;border-left:3px solid #800020;min-width:120px}
        .summary-label{font-size:10px;color:#666;text-transform:uppercase;margin-bottom:4px}
        .summary-val{font-size:16px;font-weight:600}
        table{width:100%;border-collapse:collapse;margin-bottom:15px}
        th{background:#800020;color:white;padding:7px 10px;text-align:left;font-size:10px;text-transform:uppercase}
        th.right{text-align:right}
        td{padding:6px 10px;border-bottom:1px solid #eee;font-size:11px}
        td.right{text-align:right;font-family:'Consolas',monospace}
        .severity-critical{color:#DC3545;font-weight:600}
        .severity-high{color:#FFC107;font-weight:600}
        .severity-medium{color:#17A2B8;font-weight:500}
        .severity-low{color:#28A745;font-weight:500}
        @media print{body{margin:15px}.summary-grid{page-break-inside:avoid}}
      </style></head><body>
      <h1>Inventory Intelligence Report</h1>
      <div class="meta">
        ${activeCompany?.companyName || 'Company'} |
        Financial Year: ${selectedFY?.yearName || selectedFY?.yearCode || ''} |
        ${(fromDate || toDate) ? `Date Range: ${fromDate || 'Start'} to ${toDate || 'End'} |` : ''}
        Generated: ${new Date().toLocaleString()}
      </div>
      <div class="summary-grid">
        <div class="summary-box"><div class="summary-label">Total SKUs</div><div class="summary-val">${summary.totalSKUs}</div></div>
        <div class="summary-box"><div class="summary-label">Total Stock Value</div><div class="summary-val">${formatAmount(summary.totalStockValue)}</div></div>
        <div class="summary-box"><div class="summary-label">Below Reorder</div><div class="summary-val">${summary.belowReorder}</div></div>
        <div class="summary-box"><div class="summary-label">Dead Stock Value</div><div class="summary-val">${formatAmount(summary.deadStockValue)}</div></div>
        <div class="summary-box"><div class="summary-label">Avg Turnover</div><div class="summary-val">${summary.avgTurnover.toFixed(2)}x</div></div>
        <div class="summary-box"><div class="summary-label">Active Locations</div><div class="summary-val">${summary.activeLocations}</div></div>
      </div>

      <h2>Reorder Alerts (${reorderAlerts.length})</h2>
      <table>
        <thead><tr>
          <th>Product</th><th>SKU</th><th class="right">Current Stock</th>
          <th class="right">Reorder Point</th><th class="right">Days Until Stockout</th><th>Severity</th>
        </tr></thead>
        <tbody>
          ${reorderAlerts.map(d => `
            <tr>
              <td>${d.product}</td>
              <td>${d.sku}</td>
              <td class="right">${d.currentQty}</td>
              <td class="right">${d.reorderPoint}</td>
              <td class="right">${d.daysUntilStockout === 999 ? 'N/A' : d.daysUntilStockout}</td>
              <td><span class="severity-${d.severity}">${d.severity.charAt(0).toUpperCase() + d.severity.slice(1)}</span></td>
            </tr>
          `).join('')}
        </tbody>
      </table>

      <h2>Dead Stock (${deadStockItems.length})</h2>
      <table>
        <thead><tr>
          <th>Product</th><th>SKU</th><th class="right">Value</th>
          <th class="right">Days Stagnant</th><th>Last Movement</th>
        </tr></thead>
        <tbody>
          ${deadStockItems.map(d => `
            <tr>
              <td>${d.product}</td>
              <td>${d.sku}</td>
              <td class="right">${formatAmount(d.currentValue)}</td>
              <td class="right">${d.daysSinceMovement}</td>
              <td>${formatDateStr(d.lastMovement)}</td>
            </tr>
          `).join('')}
        </tbody>
      </table>

      <h2>Category Summary</h2>
      <table>
        <thead><tr>
          <th>Category</th><th class="right">SKUs</th><th class="right">Stock %</th>
          <th class="right">Value</th><th class="right">Turnover Rate</th><th>Status</th>
        </tr></thead>
        <tbody>
          ${categorySummaryData.map(d => `
            <tr>
              <td>${d.category}</td>
              <td class="right">${d.skus}</td>
              <td class="right">${d.stockPercent}%</td>
              <td class="right">${formatAmount(d.value)}</td>
              <td class="right">${d.turnoverRate}x</td>
              <td>${d.status}</td>
            </tr>
          `).join('')}
        </tbody>
      </table>
    </body></html>`);
    addPrintBrand(w);
    w.document.close();
    w.print();
  };

  // Custom chart tooltip
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <ChartTooltipBox>
          <ChartTooltipLabel>{label}</ChartTooltipLabel>
          {payload.map((entry, index) => (
            <ChartTooltipRow key={index}>
              <ChartTooltipDot style={{ background: entry.color }} />
              <span>{entry.name}: </span>
              <strong>{typeof entry.value === 'number' && entry.name !== 'Turnover'
                ? formatCurrency(entry.value)
                : `${entry.value}${entry.name === 'Stock %' ? '%' : entry.name === 'Turnover' ? 'x' : ''}`
              }</strong>
            </ChartTooltipRow>
          ))}
        </ChartTooltipBox>
      );
    }
    return null;
  };

  const PieTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const entry = payload[0];
      return (
        <ChartTooltipBox>
          <ChartTooltipLabel>{entry.name}</ChartTooltipLabel>
          <ChartTooltipRow>
            <strong>{formatCurrency(entry.value)}</strong>
          </ChartTooltipRow>
        </ChartTooltipBox>
      );
    }
    return null;
  };

  return (
    <PageLayout pageTitle="Inventory Intelligence Report" breadcrumb="Reports / Inventory Reports / Inventory Intelligence">
      {/* Filters */}
      <FiltersCard>
        <FiltersGrid>
          <FilterGroup>
            <Label>Financial Year</Label>
            <Select value={selectedFYId} onChange={e => setSelectedFYId(e.target.value)}>
              <option value="">Select Financial Year</option>
              {financialYears.map(fy => (
                <option key={fy.id} value={fy.id}>
                  {fy.yearName || fy.yearCode}
                  {fy.isActive ? ' (Active)' : ''}
                </option>
              ))}
            </Select>
          </FilterGroup>

          <FilterGroup>
            <Label>Category</Label>
            <Select value={selectedCategory} onChange={e => setSelectedCategory(e.target.value)}>
              <option value="">All Categories</option>
              {categories.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </Select>
          </FilterGroup>

          <FilterGroup>
            <Label>From Date</Label>
            <Input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} />
          </FilterGroup>

          <FilterGroup>
            <Label>To Date</Label>
            <Input type="date" value={toDate} onChange={e => setToDate(e.target.value)} />
          </FilterGroup>

          <FilterGroup>
            <Label>Severity</Label>
            <Select value={selectedSeverity} onChange={e => setSelectedSeverity(e.target.value)}>
              <option value="All">All Severities</option>
              <option value="Critical">Critical</option>
              <option value="High">High</option>
              <option value="Medium">Medium</option>
              <option value="Low">Low</option>
            </Select>
          </FilterGroup>
        </FiltersGrid>

        <ButtonGroup>
          <Button onClick={handleExport} disabled={filteredData.length === 0}>
            <FiDownload /> Export CSV
          </Button>
          <Button onClick={handlePrint} disabled={filteredData.length === 0}>
            <FiPrinter /> Print
          </Button>
          <Button $primary onClick={loadStockFlows} disabled={loading}>
            <FiSearch /> {loading ? 'Loading...' : 'Generate Report'}
          </Button>
        </ButtonGroup>
      </FiltersCard>

      {/* Summary Cards */}
      {hasSearched && !loading && (
        <SummaryCards>
          <SummaryCard>
            <SummaryIcon $color={colors.chart.primary}><FiBox /></SummaryIcon>
            <SummaryInfo>
              <SummaryLabel>Total SKUs</SummaryLabel>
              <SummaryValue>{formatNumber(summary.totalSKUs)}</SummaryValue>
            </SummaryInfo>
          </SummaryCard>

          <SummaryCard>
            <SummaryIcon $color={colors.success.main}><FiDollarSign /></SummaryIcon>
            <SummaryInfo>
              <SummaryLabel>Total Stock Value</SummaryLabel>
              <SummaryValue style={{ color: colors.success.main }}>{formatCurrency(summary.totalStockValue)}</SummaryValue>
            </SummaryInfo>
          </SummaryCard>

          <SummaryCard>
            <SummaryIcon $color={colors.error.main}><FiAlertTriangle /></SummaryIcon>
            <SummaryInfo>
              <SummaryLabel>Items Below Reorder</SummaryLabel>
              <SummaryValue style={{ color: colors.error.main }}>{formatNumber(summary.belowReorder)}</SummaryValue>
            </SummaryInfo>
          </SummaryCard>

          <SummaryCard>
            <SummaryIcon $color={colors.warning.main}><FiClock /></SummaryIcon>
            <SummaryInfo>
              <SummaryLabel>Dead Stock Value</SummaryLabel>
              <SummaryValue style={{ color: colors.warning.main }}>{formatCurrency(summary.deadStockValue)}</SummaryValue>
            </SummaryInfo>
          </SummaryCard>

          <SummaryCard>
            <SummaryIcon $color={colors.info.main}><FiTrendingUp /></SummaryIcon>
            <SummaryInfo>
              <SummaryLabel>Avg Turnover</SummaryLabel>
              <SummaryValue>{summary.avgTurnover.toFixed(2)}x</SummaryValue>
            </SummaryInfo>
          </SummaryCard>

          <SummaryCard>
            <SummaryIcon $color={colors.chart.accent1}><FiMapPin /></SummaryIcon>
            <SummaryInfo>
              <SummaryLabel>Active Locations</SummaryLabel>
              <SummaryValue>{formatNumber(summary.activeLocations)}</SummaryValue>
            </SummaryInfo>
          </SummaryCard>
        </SummaryCards>
      )}

      {/* Table Tabs */}
      {hasSearched && !loading && (
        <ContentCard>
          <TableTabs>
            <TableTab $active={activeTable === 'reorder'} onClick={() => { setActiveTable('reorder'); setSortField('product'); setSortDirection('asc'); }}>
              Reorder Alerts ({reorderAlerts.length})
            </TableTab>
            <TableTab $active={activeTable === 'deadstock'} onClick={() => { setActiveTable('deadstock'); setSortField('product'); setSortDirection('asc'); }}>
              Dead Stock ({deadStockItems.length})
            </TableTab>
            <TableTab $active={activeTable === 'category'} onClick={() => { setActiveTable('category'); setSortField('category'); setSortDirection('asc'); }}>
              Category Summary ({categorySummaryData.length})
            </TableTab>
          </TableTabs>

          <TableContainer>
            {loading ? (
              <EmptyState>
                <EmptyIcon><FiPackage /></EmptyIcon>
                <EmptyText>Loading inventory data...</EmptyText>
              </EmptyState>
            ) : !hasSearched ? (
              <EmptyState>
                <EmptyIcon><FiPackage /></EmptyIcon>
                <EmptyText>Select filters and click "Generate Report" to view inventory intelligence</EmptyText>
              </EmptyState>
            ) : activeTable === 'reorder' ? (
              sortedReorderAlerts.length === 0 ? (
                <EmptyState>
                  <EmptyIcon><FiAlertTriangle /></EmptyIcon>
                  <EmptyText>No reorder alerts found for the selected criteria</EmptyText>
                </EmptyState>
              ) : (
                <Table>
                  <Thead>
                    <Tr>
                      <Th $sortable onClick={() => handleSort('product')}>Product{getSortIndicator('product')}</Th>
                      <Th $sortable onClick={() => handleSort('sku')}>SKU{getSortIndicator('sku')}</Th>
                      <Th $sortable $right onClick={() => handleSort('currentQty')}>Current Stock{getSortIndicator('currentQty')}</Th>
                      <Th $sortable $right onClick={() => handleSort('reorderPoint')}>Reorder Point{getSortIndicator('reorderPoint')}</Th>
                      <Th $sortable $right onClick={() => handleSort('daysUntilStockout')}>Days Until Stockout{getSortIndicator('daysUntilStockout')}</Th>
                      <Th $sortable onClick={() => handleSort('severity')}>Severity{getSortIndicator('severity')}</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {sortedReorderAlerts.map((d, index) => (
                      <Tr key={d.itemId || index}>
                        <Td>{d.product}</Td>
                        <Td>{d.sku}</Td>
                        <AmountCell $right>{formatNumber(d.currentQty)}</AmountCell>
                        <AmountCell $right>{formatNumber(d.reorderPoint)}</AmountCell>
                        <AmountCell $right>{d.daysUntilStockout === 999 ? 'N/A' : d.daysUntilStockout}</AmountCell>
                        <Td>
                          <SeverityBadge $severity={d.severity}>
                            {SEVERITY_CONFIG[d.severity]?.label || d.severity}
                          </SeverityBadge>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              )
            ) : activeTable === 'deadstock' ? (
              sortedDeadStock.length === 0 ? (
                <EmptyState>
                  <EmptyIcon><FiClock /></EmptyIcon>
                  <EmptyText>No dead stock items found for the selected criteria</EmptyText>
                </EmptyState>
              ) : (
                <Table>
                  <Thead>
                    <Tr>
                      <Th $sortable onClick={() => handleSort('product')}>Product{getSortIndicator('product')}</Th>
                      <Th $sortable onClick={() => handleSort('sku')}>SKU{getSortIndicator('sku')}</Th>
                      <Th $sortable $right onClick={() => handleSort('currentValue')}>Value{getSortIndicator('currentValue')}</Th>
                      <Th $sortable $right onClick={() => handleSort('daysSinceMovement')}>Days Stagnant{getSortIndicator('daysSinceMovement')}</Th>
                      <Th $sortable onClick={() => handleSort('lastMovement')}>Last Movement{getSortIndicator('lastMovement')}</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {sortedDeadStock.map((d, index) => (
                      <Tr key={d.itemId || index}>
                        <Td>{d.product}</Td>
                        <Td>{d.sku}</Td>
                        <AmountCell $right>{formatAmount(d.currentValue)}</AmountCell>
                        <AmountCell $right>{d.daysSinceMovement}</AmountCell>
                        <Td>{formatDateStr(d.lastMovement)}</Td>
                      </Tr>
                    ))}
                    {/* Totals Row */}
                    <TotalsRow>
                      <Td colSpan={2} style={{ textAlign: 'right', fontWeight: 700 }}>
                        Totals ({sortedDeadStock.length} items)
                      </Td>
                      <AmountCell $right style={{ fontWeight: 700 }}>
                        {formatAmount(sortedDeadStock.reduce((sum, d) => sum + (d.currentValue || 0), 0))}
                      </AmountCell>
                      <Td></Td>
                      <Td></Td>
                    </TotalsRow>
                  </Tbody>
                </Table>
              )
            ) : (
              sortedCategorySummary.length === 0 ? (
                <EmptyState>
                  <EmptyIcon><FiBox /></EmptyIcon>
                  <EmptyText>No category data found for the selected criteria</EmptyText>
                </EmptyState>
              ) : (
                <Table>
                  <Thead>
                    <Tr>
                      <Th $sortable onClick={() => handleSort('category')}>Category{getSortIndicator('category')}</Th>
                      <Th $sortable $right onClick={() => handleSort('skus')}>SKUs{getSortIndicator('skus')}</Th>
                      <Th $sortable $right onClick={() => handleSort('stockPercent')}>Stock %{getSortIndicator('stockPercent')}</Th>
                      <Th $sortable $right onClick={() => handleSort('value')}>Value{getSortIndicator('value')}</Th>
                      <Th $sortable $right onClick={() => handleSort('turnoverRate')}>Turnover Rate{getSortIndicator('turnoverRate')}</Th>
                      <Th $sortable onClick={() => handleSort('status')}>Status{getSortIndicator('status')}</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {sortedCategorySummary.map((d, index) => (
                      <Tr key={d.category || index}>
                        <Td>{d.category}</Td>
                        <AmountCell $right>{d.skus}</AmountCell>
                        <AmountCell $right>{d.stockPercent}%</AmountCell>
                        <AmountCell $right>{formatAmount(d.value)}</AmountCell>
                        <AmountCell $right>{d.turnoverRate}x</AmountCell>
                        <Td>
                          <StatusBadge $status={d.status}>
                            {d.status}
                          </StatusBadge>
                        </Td>
                      </Tr>
                    ))}
                    {/* Totals Row */}
                    <TotalsRow>
                      <Td style={{ textAlign: 'right', fontWeight: 700 }}>
                        Totals ({sortedCategorySummary.length} categories)
                      </Td>
                      <AmountCell $right style={{ fontWeight: 700 }}>
                        {sortedCategorySummary.reduce((sum, d) => sum + d.skus, 0)}
                      </AmountCell>
                      <AmountCell $right style={{ fontWeight: 700 }}>100%</AmountCell>
                      <AmountCell $right style={{ fontWeight: 700 }}>
                        {formatAmount(sortedCategorySummary.reduce((sum, d) => sum + d.value, 0))}
                      </AmountCell>
                      <AmountCell $right style={{ fontWeight: 700 }}>
                        {summary.avgTurnover.toFixed(2)}x
                      </AmountCell>
                      <Td></Td>
                    </TotalsRow>
                  </Tbody>
                </Table>
              )
            )}
          </TableContainer>
        </ContentCard>
      )}

      {/* Charts */}
      {hasSearched && !loading && filteredData.length > 0 && (
        <ChartsRow>
          {/* Stock Levels by Category */}
          <ChartSection>
            <ChartTitle>Stock Levels by Category</ChartTitle>
            {categoryStockData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={categoryStockData} margin={{ top: 10, right: 30, left: 10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={colors.neutral[200]} />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} stroke={colors.text.secondary} />
                  <YAxis tick={{ fontSize: 11 }} stroke={colors.text.secondary} tickFormatter={(v) => `${v}%`} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="stockPercent" name="Stock %" fill={colors.chart.primary} radius={[4, 4, 0, 0]} barSize={30} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <ChartEmpty>No category stock data available</ChartEmpty>
            )}
          </ChartSection>

          {/* Inventory Aging */}
          <ChartSection>
            <ChartTitle>Inventory Aging</ChartTitle>
            {agingData.some(d => d.value > 0) ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={agingData}
                    cx="50%"
                    cy="50%"
                    outerRadius={100}
                    innerRadius={50}
                    dataKey="value"
                    nameKey="name"
                    label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                    labelLine={{ stroke: colors.text.secondary, strokeWidth: 1 }}
                  >
                    {agingData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip content={<PieTooltip />} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <ChartEmpty>No aging data available</ChartEmpty>
            )}
          </ChartSection>

          {/* Turnover by Category */}
          <ChartSection>
            <ChartTitle>Turnover by Category</ChartTitle>
            {turnoverData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={turnoverData} margin={{ top: 10, right: 30, left: 10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={colors.neutral[200]} />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} stroke={colors.text.secondary} />
                  <YAxis tick={{ fontSize: 11 }} stroke={colors.text.secondary} tickFormatter={(v) => `${v}x`} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="turnover" name="Turnover" fill={colors.chart.accent2} radius={[4, 4, 0, 0]} barSize={30} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <ChartEmpty>No turnover data available</ChartEmpty>
            )}
          </ChartSection>

          {/* Stock by Location */}
          <ChartSection>
            <ChartTitle>Stock by Location</ChartTitle>
            {locationData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={locationData}
                    cx="50%"
                    cy="50%"
                    outerRadius={100}
                    innerRadius={50}
                    dataKey="value"
                    nameKey="name"
                    label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                    labelLine={{ stroke: colors.text.secondary, strokeWidth: 1 }}
                  >
                    {locationData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip content={<PieTooltip />} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <ChartEmpty>No location data available</ChartEmpty>
            )}
          </ChartSection>
        </ChartsRow>
      )}
    </PageLayout>
  );
};

// ============ STYLED COMPONENTS ============

const FiltersCard = styled.div`
  background: white;
  border-radius: ${colors.borderRadius.lg};
  box-shadow: ${colors.shadows.sm};
  padding: 20px;
  margin-bottom: 20px;
`;

const FiltersGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 14px;
  margin-bottom: 16px;
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.label`
  font-size: 11px;
  font-weight: 600;
  color: ${colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.3px;
`;

const Select = styled.select`
  padding: 9px 12px;
  border: 1px solid ${colors.neutral[300]};
  border-radius: ${colors.borderRadius.md};
  font-size: 13px;
  color: ${colors.text.primary};
  background: white;
  cursor: pointer;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: ${colors.primary.main};
    box-shadow: 0 0 0 3px ${colors.primary.main}15;
  }
`;

const Input = styled.input`
  padding: 9px 12px;
  border: 1px solid ${colors.neutral[300]};
  border-radius: ${colors.borderRadius.md};
  font-size: 13px;
  color: ${colors.text.primary};
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: ${colors.primary.main};
    box-shadow: 0 0 0 3px ${colors.primary.main}15;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-end;
`;

const Button = styled.button`
  padding: 9px 18px;
  border: ${props => props.$primary ? 'none' : `1px solid ${colors.neutral[300]}`};
  border-radius: ${colors.borderRadius.md};
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 7px;
  transition: all 0.2s;

  ${props => props.$primary ? `
    background: ${colors.primary.main};
    color: white;
    &:hover { background: ${colors.primary.dark}; }
  ` : `
    background: white;
    color: ${colors.text.primary};
    &:hover { background: ${colors.neutral[100]}; border-color: ${colors.primary.main}; }
  `}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const SummaryCards = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 20px;
`;

const SummaryCard = styled.div`
  background: white;
  border-radius: ${colors.borderRadius.lg};
  box-shadow: ${colors.shadows.sm};
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 16px;
`;

const SummaryIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: ${colors.borderRadius.md};
  background: ${props => props.$color || colors.primary.main}15;
  color: ${props => props.$color || colors.primary.main};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  flex-shrink: 0;
`;

const SummaryInfo = styled.div`
  min-width: 0;
`;

const SummaryLabel = styled.div`
  font-size: 12px;
  color: ${colors.text.secondary};
  margin-bottom: 4px;
  white-space: nowrap;
`;

const SummaryValue = styled.div`
  font-size: 20px;
  font-weight: 600;
  color: ${colors.text.primary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ChartsRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-bottom: 20px;

  @media (max-width: 1200px) {
    grid-template-columns: 1fr;
  }
`;

const ChartSection = styled.div`
  background: white;
  border-radius: ${colors.borderRadius.lg};
  box-shadow: ${colors.shadows.sm};
  padding: 20px;

  ${props => props.$wide && `
    grid-column: 1 / -1;
  `}
`;

const ChartTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  color: ${colors.text.primary};
  margin: 0 0 16px 0;
`;

const ChartEmpty = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 200px;
  color: ${colors.text.tertiary};
  font-size: 14px;
`;

const ChartTooltipBox = styled.div`
  background: white;
  border: 1px solid ${colors.neutral[200]};
  border-radius: ${colors.borderRadius.md};
  padding: 10px 14px;
  box-shadow: ${colors.shadows.md};
`;

const ChartTooltipLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: ${colors.text.primary};
  margin-bottom: 6px;
`;

const ChartTooltipRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: ${colors.text.secondary};
  margin-bottom: 2px;

  strong {
    color: ${colors.text.primary};
  }
`;

const ChartTooltipDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
`;

const ContentCard = styled.div`
  background: white;
  border-radius: ${colors.borderRadius.lg};
  box-shadow: ${colors.shadows.sm};
  overflow: hidden;
`;

const TableTabs = styled.div`
  display: flex;
  border-bottom: 2px solid ${colors.neutral[200]};
  padding: 0 16px;
`;

const TableTab = styled.button`
  padding: 14px 20px;
  font-size: 13px;
  font-weight: 600;
  color: ${props => props.$active ? colors.primary.main : colors.text.secondary};
  background: transparent;
  border: none;
  border-bottom: 2px solid ${props => props.$active ? colors.primary.main : 'transparent'};
  margin-bottom: -2px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    color: ${colors.primary.main};
  }
`;

const TableContainer = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Thead = styled.thead`
  background: ${colors.neutral[50]};
`;

const Th = styled.th`
  padding: 12px 14px;
  text-align: ${props => props.$right ? 'right' : 'left'};
  font-size: 11px;
  font-weight: 600;
  color: ${colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 2px solid ${colors.neutral[200]};
  white-space: nowrap;
  user-select: none;

  ${props => props.$sortable && `
    cursor: pointer;
    transition: color 0.2s;
    &:hover {
      color: ${colors.primary.main};
    }
  `}
`;

const Tbody = styled.tbody``;

const Tr = styled.tr`
  border-bottom: 1px solid ${colors.neutral[100]};
  transition: background 0.15s;

  &:hover {
    background: ${colors.neutral[50]};
  }
`;

const TotalsRow = styled.tr`
  background: ${colors.neutral[100]};
  border-top: 2px solid ${colors.primary.main};

  td {
    padding: 14px;
  }

  &:hover {
    background: ${colors.neutral[100]};
  }
`;

const Td = styled.td`
  padding: 10px 14px;
  font-size: 13px;
  color: ${colors.text.primary};
  text-align: ${props => props.$right ? 'right' : 'left'};
`;

const AmountCell = styled(Td)`
  font-family: 'SF Mono', 'Consolas', 'Courier New', monospace;
  font-size: 13px;
  text-align: right;
  color: ${props => {
    if (props.$type === 'critical') return colors.error.main;
    if (props.$type === 'warning') return colors.warning.main;
    return colors.text.primary;
  }};
`;

const SeverityBadge = styled.span`
  display: inline-block;
  padding: 3px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 500;
  background: ${props => SEVERITY_CONFIG[props.$severity]?.bg || colors.neutral[100]};
  color: ${props => SEVERITY_CONFIG[props.$severity]?.color || colors.text.secondary};
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 3px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 500;
  background: ${props => {
    if (props.$status === 'Healthy') return '#E8F5E9';
    if (props.$status === 'Normal') return '#E3F2FD';
    if (props.$status === 'Slow') return '#FFF3E0';
    return colors.neutral[100];
  }};
  color: ${props => {
    if (props.$status === 'Healthy') return colors.success.main;
    if (props.$status === 'Normal') return colors.info.main;
    if (props.$status === 'Slow') return colors.warning.dark;
    return colors.text.secondary;
  }};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: ${colors.text.secondary};
`;

const EmptyIcon = styled.div`
  font-size: 48px;
  color: ${colors.neutral[300]};
  margin-bottom: 16px;
`;

const EmptyText = styled.p`
  font-size: 16px;
  margin-bottom: 8px;
`;

export default InventoryIntelligenceReport;
